import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { SwitchUserRootParamList } from "../../../../types";
import {
  Button,
  ModalCloseButton,
  Page,
} from "@towersystems/roam-common-native";
import React from "react";
import { useCurrentVendorProvider } from "../../../../utilities/vendor/hooks";
import { SwitchUserFormValues } from "../../../../components/SwitchUserForm/types";
import { SwitchUserFormProvider } from "../../../../components/SwitchUserForm/SwitchUserFormProvider";
import { useFormContext } from "react-hook-form";
import { SwitchUserForm } from "../../../../components/SwitchUserForm";
import { MfaCodeStrategy } from "@towersystems/roam-common/lib/generated-types";
import { useAuthenticationProvider } from "@towersystems/roam-common-native/lib/utilities/authentication";
import { SwitchUserFormAdditionalActionsContext } from "../../../../components/SwitchUserForm/context";

export type SwitchUserEnterCredentialsScreenProps = NativeStackScreenProps<
  SwitchUserRootParamList,
  "SwitchUserEnterCredentialsScreen"
>;

interface SwitchUserProps extends SwitchUserEnterCredentialsScreenProps {}

const SwitchUser = ({ navigation }: SwitchUserProps) => {
  const { handleSubmit, setValue, watch, setError, formState } =
    useFormContext<SwitchUserFormValues>();

  const { switchUser, requestMfaCode, me } = useAuthenticationProvider();
  const { isSubmitting, isValid } = formState;
  const isDisabled = isSubmitting || !isValid;

  const mfaRequired = watch("mfaRequired");

  const handleOnSubmit = React.useCallback((values: SwitchUserFormValues) => {
    if (values.mfaRequired && values.mfaCodeSent == false) {
      return requestMfaCode(
        {
          strategy: values.authStrategy,
          talinkUsername: values.talinkUsername,
          staffUsername: values.identifier,
          password: values.password,
          rememberMe: false,
        },
        values.mfaStrategy as MfaCodeStrategy
      )
        .then(() => {
          setValue("mfaCodeSent", true);
        })
        .catch((err) => {
          setError("root.serverError", {
            type: "400",
            message: "Something went wrong.",
          });
        });
    }

    try {
      let authInput;
      let mfaInput;

      if (values.authStrategy === "native") {
        authInput = {
          native: {
            talinkUsername: values.talinkUsername,
            username: values.identifier,
            password: values.password,
          },
        };
      } else {
        authInput = {
          talink: {
            talinkUsername: values.talinkUsername,
            password: values.password,
          },
        };
      }

      if (values.mfaCode && values.mfaStrategy) {
        mfaInput = {
          mfaCode: values.mfaCode,
          strategy: values.mfaStrategy,
        };
      }

      return switchUser(authInput, mfaInput)
        .then((result) => {
          if ("errorCode" in result) {
            if (
              result.errorCode === "MFA_REQUIRED_ERROR" &&
              (result as any).strategies
            ) {
              setValue("mfaRequired", true);
              setValue("mfaStrategies", (result as any).strategies);
              return;
            }

            if (result.errorCode === "INVALID_MFA_CODE_ERROR") {
              setError("mfaCode", {
                type: "a",
                message: "Invalid MFA Code",
              });
              return;
            }

            if (result.errorCode === "INVALID_CREDENTIALS_ERROR") {
              setError("password", {
                type: "a",
                message: "Invalid Password",
              });
              return;
            } else {
              setError("root.serverError", {
                type: "400",
                message: result.errorCode,
              });
            }
          } else {
            return (
              me()
                .then(() => window.location.reload())
                // something went wrong, best to reload the app and refresh memory
                .catch(() => window.location.reload())
            );
          }
        })
        .catch((err) => {
          setError("root.serverError", {
            type: "400",
            message: "Something went wrong.",
          });
        });
    } catch (err) {
      setError("root.serverError", {
        type: "400",
        message: "Something went wrong.",
      });
    }
  }, []);

  React.useEffect(() => {
    navigation.setOptions({
      headerTitleAlign: "center",
      headerTitle: mfaRequired ? "MFA Required" : "Enter Credentials",
      headerLeft: () => <ModalCloseButton type="Back" />,

      headerRight: () => {
        return (
          <Button
            label="Submit"
            isAction
            disabled={isDisabled}
            onPress={handleSubmit(handleOnSubmit)}
            size="compact"
          ></Button>
        );
      },
    });
  }, [isValid, isDisabled, mfaRequired, handleOnSubmit]);

  return (
    <SwitchUserFormAdditionalActionsContext.Provider
      value={{
        onSubmitEditingAll: handleSubmit(handleOnSubmit),
      }}
    >
      <SwitchUserForm />
    </SwitchUserFormAdditionalActionsContext.Provider>
  );
};

export const SwitchUserEnterCredentialsScreen = ({
  ...props
}: SwitchUserEnterCredentialsScreenProps) => {
  const { currentVendor } = useCurrentVendorProvider();
  const { route } = props;
  const { username } = route.params;
  const authStrategy = !username ? "talink" : "native";

  return (
    <Page scrollable={false} cover>
      <SwitchUserFormProvider
        talinkUsername={currentVendor.talinkUsername ?? "N/A"}
        identifier={username}
        authStrategy={authStrategy}
      >
        <SwitchUser {...props} />
      </SwitchUserFormProvider>
    </Page>
  );
};
